import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// Providers e layout — carregam sempre (necessários no boot)
import LoggedInLayout from "../layout";
import { AuthProvider } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { ImpersonationProvider } from "../context/Impersonation/ImpersonationContext";
import ImpersonationBanner from "../components/ImpersonationBanner";
import DemoBanner from "../components/DemoBanner";
import Route from "./Route";

// Páginas públicas — carregam junto (pequenas, caminho crítico)
import Login from "../pages/Login";
import Signup from "../pages/Signup";
import ForgotPassword from "../pages/ForgetPassWord";
import ResetPassword from "../pages/ResetPassword";
import PartnerPortal from "../pages/Partner";

// Páginas privadas — lazy: cada chunk carrega só quando o usuário navegar
const Dashboard               = React.lazy(() => import("../pages/Dashboard"));
const TicketResponsiveContainer = React.lazy(() => import("../pages/TicketResponsiveContainer"));
const Connections             = React.lazy(() => import("../pages/Connections"));
const SettingsCustom          = React.lazy(() => import("../pages/SettingsCustom"));
const Financeiro              = React.lazy(() => import("../pages/Financeiro"));
const Users                   = React.lazy(() => import("../pages/Users"));
const Contacts                = React.lazy(() => import("../pages/Contacts"));
const ContactImportPage       = React.lazy(() => import("../pages/Contacts/import"));
const ChatMoments             = React.lazy(() => import("../pages/Moments"));
const Queues                  = React.lazy(() => import("../pages/Queues"));
const Tags                    = React.lazy(() => import("../pages/Tags"));
const MessagesAPI             = React.lazy(() => import("../pages/MessagesAPI"));
const Helps                   = React.lazy(() => import("../pages/Helps"));
const ContactLists            = React.lazy(() => import("../pages/ContactLists"));
const ContactListItems        = React.lazy(() => import("../pages/ContactListItems"));
const Companies               = React.lazy(() => import("../pages/Companies"));
const OwnerDashboard          = React.lazy(() => import("../pages/Owner"));
const PlanosPage              = React.lazy(() => import("../pages/Planos"));
const QuickMessages           = React.lazy(() => import("../pages/QuickMessages"));
const Schedules               = React.lazy(() => import("../pages/Schedules"));
const Campaigns               = React.lazy(() => import("../pages/Campaigns"));
const CampaignsConfig         = React.lazy(() => import("../pages/CampaignsConfig"));
const CampaignReport          = React.lazy(() => import("../pages/CampaignReport"));
const Annoucements            = React.lazy(() => import("../pages/Annoucements"));
const Chat                    = React.lazy(() => import("../pages/Chat"));
const Prompts                 = React.lazy(() => import("../pages/Prompts"));
const AllConnections          = React.lazy(() => import("../pages/AllConnections"));
const Reports                 = React.lazy(() => import("../pages/Reports"));
const FlowBuilderConfig       = React.lazy(() => import("../pages/FlowBuilderConfig").then(m => ({ default: m.FlowBuilderConfig })));
const FlowBuilder             = React.lazy(() => import("../pages/FlowBuilder"));
const FlowDefault             = React.lazy(() => import("../pages/FlowDefault"));
const CampaignsPhrase         = React.lazy(() => import("../pages/CampaignsPhrase"));
const Subscription            = React.lazy(() => import("../pages/Subscription"));
const QueueIntegration        = React.lazy(() => import("../pages/QueueIntegration"));
const Files                   = React.lazy(() => import("../pages/Files"));
const ToDoList                = React.lazy(() => import("../pages/ToDoList"));
const Kanban                  = React.lazy(() => import("../pages/Kanban"));
const TagsKanban              = React.lazy(() => import("../pages/TagsKanban"));
const GlobalConfig            = React.lazy(() => import("../pages/GlobalConfig"));
const AgentPerformance        = React.lazy(() => import("../pages/AgentPerformance"));
const BusinessIntelligencePage = React.lazy(() => import("../pages/BusinessIntelligence"));
const ImpersonationPage       = React.lazy(() => import("../pages/Impersonation"));
const AdminPartners           = React.lazy(() => import("../pages/Partners"));
const FollowUpSettings        = React.lazy(() => import("../pages/FollowUpSettings"));
const SalesAgentPage          = React.lazy(() => import("../pages/SalesAgent"));
const AgendaInfinite           = React.lazy(() => import("../pages/AgendaInfinite"));
const HelpPage                = React.lazy(() => import("../pages/Help"));

// Spinner minimalista enquanto o chunk carrega
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
    <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#4F46E5", animation: "spin .8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Routes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <TicketsContextProvider>
          <Switch>
            <Route exact path="/login" component={Login} />
            <Route exact path="/representante" component={PartnerPortal} />
            <Route exact path="/signup" component={Signup} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <Route exact path="/reset-password" component={ResetPassword} />
            <WhatsAppsProvider>
              <ImpersonationProvider>
                <ImpersonationBanner />
                <DemoBanner />
              <LoggedInLayout>
                <Suspense fallback={<PageLoader />}>
                  <Route exact path="/financeiro" component={Financeiro} isPrivate />
                  <Route exact path="/companies" component={Companies} isPrivate />
                  <Route exact path="/owner" component={OwnerDashboard} isPrivate />
                  <Route exact path="/planos" component={PlanosPage} isPrivate />
                  <Route exact path="/" component={Dashboard} isPrivate />
                  <Route exact path="/tickets/:ticketId?" component={TicketResponsiveContainer} isPrivate />
                  <Route exact path="/connections" component={Connections} isPrivate />
                  <Route exact path="/quick-messages" component={QuickMessages} isPrivate />
                  <Route exact path="/todolist" component={ToDoList} isPrivate />
                  <Route exact path="/schedules" component={Schedules} isPrivate />
                  <Route exact path="/tags" component={Tags} isPrivate />
                  <Route exact path="/contacts" component={Contacts} isPrivate />
                  <Route exact path="/contacts/import" component={ContactImportPage} isPrivate />
                  <Route exact path="/helps" component={Helps} isPrivate />
                  <Route exact path="/users" component={Users} isPrivate />
                  <Route exact path="/messages-api" component={MessagesAPI} isPrivate />
                  <Route exact path="/settings" component={SettingsCustom} isPrivate />
                  <Route exact path="/global-config" component={GlobalConfig} isPrivate />
                  <Route exact path="/queues" component={Queues} isPrivate />
                  <Route exact path="/reports" component={Reports} isPrivate />
                  <Route exact path="/queue-integration" component={QueueIntegration} isPrivate />
                  <Route exact path="/announcements" component={Annoucements} isPrivate />
                  <Route exact path="/phrase-lists" component={CampaignsPhrase} isPrivate />
                  <Route exact path="/flowbuilders" component={FlowBuilder} isPrivate />
                  <Route exact path="/flowbuilder/:id?" component={FlowBuilderConfig} isPrivate />
                  <Route exact path="/chats/:id?" component={Chat} isPrivate />
                  <Route exact path="/files" component={Files} isPrivate />
                  <Route exact path="/moments" component={ChatMoments} isPrivate />
                  <Route exact path="/Kanban" component={Kanban} isPrivate />
                  <Route exact path="/TagsKanban" component={TagsKanban} isPrivate />
                  <Route exact path="/prompts" component={Prompts} isPrivate />
                  <Route exact path="/allConnections" component={AllConnections} isPrivate />
                  <Route exact path="/agent-performance" component={AgentPerformance} isPrivate />
                  <Route exact path="/business-intelligence" component={BusinessIntelligencePage} isPrivate />
                  <Route exact path="/support" component={ImpersonationPage} isPrivate />
                  <Route exact path="/parceiros" component={AdminPartners} isPrivate />
                  <Route exact path="/seguimento-automatico" component={FollowUpSettings} isPrivate />
              <Route exact path="/agenda-infinita" isPrivate component={AgendaInfinite} />
              <Route exact path="/sales-agent" component={SalesAgentPage} isPrivate />


                  <Route exact path="/ajuda" component={HelpPage} isPrivate />
                  {showCampaigns && (
                    <>
                      <Route exact path="/contact-lists" component={ContactLists} isPrivate />
                      <Route exact path="/contact-lists/:contactListId/contacts" component={ContactListItems} isPrivate />
                      <Route exact path="/campaigns" component={Campaigns} isPrivate />
                      <Route exact path="/campaign/:campaignId/report" component={CampaignReport} isPrivate />
                      <Route exact path="/campaigns-config" component={CampaignsConfig} isPrivate />
                    </>
                  )}
                </Suspense>
              </LoggedInLayout>
              </ImpersonationProvider>
            </WhatsAppsProvider>
          </Switch>
          <ToastContainer position="top-center" autoClose={3000} />
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
